import React from 'react';

import { Empty, List, Typography } from 'antd';

import { getAssetTypeConfig } from '@/shared/api/openmetadata/constants';
import { convertFqnToPath } from '@/shared/lib/format';
import { highlightText } from '@/shared/lib/text';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { Icon } from '@/shared/ui/Icon';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import { EXPERIMENT_RELATED_TYPES } from '@/entities/asset-search';
import { useFilterStore } from '@/entities/discovery';
import type { SearchResult } from '@/entities/search';

import { useSearchSuggestionsData } from '@/features/dataSearch/model/useSearchSuggestionsData';

import styles from './SearchResultSuggestion.module.css';

const { Text } = Typography;

type SearchResultSuggestionProps = {
  debouncedValue: string;
  onClickSuggestion: (suggestion: SearchResult) => void;
  onClickViewAllResults: () => void;
};

export const SearchResultSuggestion = ({
  debouncedValue,
  onClickSuggestion,
  onClickViewAllResults,
}: SearchResultSuggestionProps) => {
  const { filters } = useFilterStore();
  const { searchSuggestions, isLoading, isError, isPaused, error, refetch } = useSearchSuggestionsData({
    searchQuery: debouncedValue,
    filters,
  });

  if (isError || isPaused) {
    return (
      <ErrorFallback
        error={error}
        resetErrorBoundary={() => refetch()}
        description="검색에 실패했습니다. 다시 시도해 주세요."
      />
    );
  }

  if (isLoading) {
    return <LoadingState message="검색 중..." size={24} compact />;
  }

  if (searchSuggestions.length === 0) {
    const trimmedDebouncedValue = debouncedValue.trim();
    const noResultsMessage = trimmedDebouncedValue
      ? `"${trimmedDebouncedValue}"에 대한 검색 결과가 없습니다`
      : '검색 결과가 없습니다';

    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={noResultsMessage} className={styles.empty} />;
  }

  return (
    <>
      <Text className={styles.sectionTitle}>검색 제안</Text>
      <List
        size="small"
        dataSource={searchSuggestions}
        renderItem={(item) => {
          const assetTypeConfig = getAssetTypeConfig(item.type);
          const backgroundColor = assetTypeConfig.tagBackgroundColor || 'var(--opds-glo-palette-gray-98)';
          const borderColor = assetTypeConfig.tagBackgroundColor || 'var(--opds-glo-palette-gray-90)';
          const iconColor = assetTypeConfig.color || 'var(--opds-glo-palette-gray-50)';

          const iconWrapperStyle = {
            backgroundColor,
            border: `1px solid ${borderColor}`,
          };

          const isExperimentRelated = EXPERIMENT_RELATED_TYPES.some((t) => t === item.type);

          let pathParts: string[];
          if (isExperimentRelated && item._source?.assetPath) {
            const shouldIncludeSelf = item.type === 'artifact';
            pathParts = item._source.assetPath
              .filter((p) => shouldIncludeSelf || p.type !== item.type)
              .map((p) => p.label);
          } else {
            const fullPath = convertFqnToPath(item.path);
            const parts = fullPath.split(' / ');
            pathParts = parts.length > 1 ? parts.slice(0, -1) : [fullPath];
          }

          const maxWidthPerPart = `${100 / pathParts.length}%`;

          return (
            <List.Item className={styles.listItem} key={item.id} onClick={() => onClickSuggestion(item)}>
              <div className={styles.suggestionContent}>
                <div className={styles.iconWrapper} style={iconWrapperStyle}>
                  <Icon assetType={item.type} size={13} color={iconColor} />
                </div>
                <div className={styles.suggestionInfo}>
                  <div className={styles.suggestionPath}>
                    {pathParts.map((part, index) => (
                      <React.Fragment key={index}>
                        <div className={styles.pathPart} style={{ maxWidth: maxWidthPerPart }}>
                          <EllipsisText text={part}>
                            {highlightText(part, debouncedValue, { className: 'highlight' })}
                          </EllipsisText>
                        </div>
                        {index < pathParts.length - 1 && <span className={styles.pathSeparator}>/</span>}
                      </React.Fragment>
                    ))}
                  </div>
                  <Text className={styles.suggestionTitle}>
                    {highlightText(item.title, debouncedValue, { className: 'highlight' })}
                  </Text>
                </div>
              </div>
            </List.Item>
          );
        }}
      />
      <div className={styles.viewAllButtonContainer}>
        <button className={styles.viewAllButton} onClick={onClickViewAllResults}>
          <Icon name="search" size={13} />
          <span>모든 결과 보기</span>
        </button>
      </div>
    </>
  );
};
